.bar {
  display: inline-flex;
  gap: 4px;
  background: var(--glass-bg, var(--bg-surface));
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border, var(--border-default));
  border-radius: 24px;
  padding: 3px;
}

/* Light mode: slightly darker background for better contrast */
:global([data-theme='light']) .bar {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}

.option {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: var(--weight-medium);
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s;
  font-family: var(--font-sans);
  white-space: nowrap;
  position: relative;
}

.option:hover {
  color: var(--accent-secondary-text);
  background: rgba(240, 180, 41, 0.08);
}

:global([data-theme='light']) .option:hover {
  background: rgba(180, 130, 20, 0.12);
  color: #9a7000;
}

.optionActive {
  background: linear-gradient(135deg, rgba(212, 168, 67, 0.7), rgba(140, 100, 20, 0.7));
  color: #fff;
  font-weight: 900;
}

/* Light mode: more opaque gold for better visibility */
:global([data-theme='light']) .optionActive {
  background: linear-gradient(135deg, #c9a033, #8c6414);
  color: #fff;
}

.optionActive::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 20px;
  padding: 1px;
  background: linear-gradient(315deg, rgba(212, 168, 67, 1), rgba(140, 100, 20, 1));
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

:global([data-theme='light']) .optionActive::before {
  background: linear-gradient(315deg, #d4a843, #8c6414);
}

/* Count pill after the label. Neutral by default; on the active (gold) option
   it switches to a translucent-white chip. */
.count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  padding: 0 5px;
  border-radius: var(--radius-pill);
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  font-size: 10px;
  font-weight: var(--weight-bold);
  line-height: 1.6;
  color: var(--text-muted);
}

.optionActive .count {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  color: #fff;
}

:global([data-theme='light']) .optionActive .count {
  background: rgba(0, 0, 0, 0.15);
  border-color: rgba(0, 0, 0, 0.2);
  color: #fff;
}

/* ── 'field' variant: matches the Select trigger box ───────────────────── */
.field {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 36px;
  padding: 3px;
  box-sizing: border-box;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  transition: border-color var(--duration-fast) var(--ease-default);
}

.fieldSet {
  border-color: color-mix(in srgb, var(--active) 55%, var(--input-border));
}

.fieldOption {
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding: 0 var(--space-3);
  border-radius: calc(var(--radius-md) - 3px);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-muted);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
}

.fieldOption:hover {
  color: var(--text-primary);
}

.fieldOption:focus-visible {
  box-shadow: 0 0 0 2px var(--active-muted);
}

.fieldOptionActive {
  background: var(--bg-active);
  color: var(--text-primary);
}

.fieldSet .fieldOptionActive {
  color: var(--active);
}

/* ── disabled: greyed + inert ──────────────────────────────────────────────
   Used when a custom portfolio scope is active — content-type scoping is then
   mutually exclusive, so the whole control is dimmed and non-interactive. */
.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.disabled .option,
.disabled .fieldOption {
  cursor: not-allowed;
  pointer-events: none;
  color: var(--text-muted);
}

.disabled .option:hover,
.disabled .fieldOption:hover {
  background: transparent;
  color: var(--text-muted);
}
